/* Green Gorilla Fruits — referral redeem after signup
 * --------------------------------------------------------------
 * When a customer is logged in and a stored referral code from
 * referral-link.js is present, POSTs once to the App Proxy
 * /api/referral/redeem endpoint. On success the returned coupon
 * code is handed to coupon-autoapply.js via LocalStorage.
 *
 * Public API: window.GG_ReferralRedeem.run()
 */
(function () {
  'use strict';

  const COUPON_STORAGE_KEY = 'gg_referral_coupon';
  const ATTEMPT_FLAG_KEY = 'gg_referral_redeem_attempted';
  const DEBUG_KEY = 'gg_debug';
  const LOG_PREFIX = '[gg-referral][redeem]';

  function debugEnabled() {
    try {
      return localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function log() {
    if (!debugEnabled()) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    try {
      // eslint-disable-next-line no-console
      console.log.apply(console, args);
    } catch (e) {
      /* ignore */
    }
  }

  function getConfig() {
    return window.GG_REFERRAL_CONFIG && typeof window.GG_REFERRAL_CONFIG === 'object'
      ? window.GG_REFERRAL_CONFIG
      : {};
  }

  function getReferralCode() {
    if (window.GG_Referral && typeof window.GG_Referral.getCode === 'function') {
      return window.GG_Referral.getCode();
    }
    return null;
  }

  function clearReferralCode() {
    if (window.GG_Referral && typeof window.GG_Referral.clear === 'function') {
      window.GG_Referral.clear();
    }
  }

  function markAttempted() {
    try {
      sessionStorage.setItem(ATTEMPT_FLAG_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }

  function alreadyAttempted() {
    try {
      return sessionStorage.getItem(ATTEMPT_FLAG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function persistCoupon(payload) {
    try {
      localStorage.setItem(COUPON_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
  }

  function buildUrl(base) {
    const normalized = (base || '/apps/referral').replace(/\/+$/, '');
    return normalized + '/api/referral/redeem';
  }

  let inFlight = false;

  async function run() {
    const cfg = getConfig();
    const ca = window.GG_CustomerAccount;
    if (!ca || typeof ca.getStatus !== 'function') {
      log('run: GG_CustomerAccount missing — skip');
      return;
    }
    if (alreadyAttempted()) {
      log('run: already attempted this session — skip');
      return;
    }
    if (inFlight) {
      log('run: already in-flight — skip');
      return;
    }
    const code = getReferralCode();
    if (!code) {
      log('run: no stored referral code — skip');
      return;
    }
    log('run: starting redeem flow', { code: code });

    inFlight = true;
    try {
      const status = await ca.getStatus();
      log('run: getStatus resolved', status);
      if (!status.loggedIn) return;

      const authHeader = typeof ca.getAuthHeader === 'function' ? ca.getAuthHeader() : {};
      const url = buildUrl(cfg.appProxyBase);
      log('run: POSTing redeem', { url: url, hasAuthHeader: !!authHeader.Authorization });
      const res = await fetch(url, {
        method: 'POST',
        headers: Object.assign(
          {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          authHeader
        ),
        body: JSON.stringify({ referral_code: code }),
      });

      if (res.ok) {
        const data = await res.json();
        log('run: redeem 200', { body: data });
        persistCoupon({
          code: data.coupon_code,
          amount_usd: data.amount_usd,
          expires_at: data.expires_at,
        });
        clearReferralCode();
        markAttempted();
        // coupon-autoapply.js already executed at DOMContentLoaded with no
        // coupon present (we just stored it). Re-trigger here so the visitor
        // that landed on /cart through the handshake redirect is immediately
        // forwarded to /discount/<code> on the same page load.
        if (window.GG_CouponAutoApply && typeof window.GG_CouponAutoApply.run === 'function') {
          log('run: redeem 200 — re-triggering coupon-autoapply');
          // run() is synchronous and calls window.location.replace() when a
          // redirect is required, so control normally does not return here.
          window.GG_CouponAutoApply.run();
        }
        return;
      }

      if (res.status === 400 || res.status === 404 || res.status === 409) {
        // self_referral / invalid / not_found / already_redeemed — stop retrying.
        const body = await res.json().catch(() => ({}));
        log('run: redeem terminal error', { status: res.status, body: body });
        clearReferralCode();
        markAttempted();
        return;
      }

      if (res.status === 401) {
        // invalid_token → drop the JWT so the next page load probes fresh.
        // missing_token / 5xx → leave the referral code in place and retry next page load.
        const body = await res.json().catch(() => ({}));
        log('run: redeem 401', { body: body });
        if (body && body.code === 'invalid_token' && typeof ca.clearSession === 'function') {
          ca.clearSession();
        }
        return;
      }

      // Other transient errors (5xx) — leave the code in place and retry next page load.
      log('run: redeem transient error', { status: res.status });
    } catch (e) {
      log('run: network error', { error: String(e) });
      /* network error — leave the code in place for next page load */
    } finally {
      inFlight = false;
    }
  }

  window.GG_ReferralRedeem = { run };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
